import React, { useMemo, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import toLower from 'lodash/toLower'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { PropertyActions } from '@galo-monitor/stores/property'
import { selectCurrentProperty, selectUnitOfMeasurement } from '@galo-monitor/stores/property/selectorProperty'
import { PLOT_CLASSIFICATION_TYPES } from '@galo-monitor/utils/constants'
import DataTable from '@galo-monitor/web-components/DataTable'
import ConfirmationModal from '@galo-monitor/web-containers/modals/ConfirmationModal'
import ErrorModal from '@galo-monitor/web-containers/modals/ErrorModal'
import ImportMapModal from '@galo-monitor/web-containers/modals/farm/ImportMapModal'
import PlotModal from '@galo-monitor/web-containers/modals/farm/PlotModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'


const Plots = () => {
  const t = useT()
  const snackbar = useSnackbar()
  const { createDialog } = useDialog()
  const dispatch = useCallback(useDispatch(), [])
  const { routes, getActionsBySlug } = useRoutes()

  const currentProperty = useSelector(selectCurrentProperty)
  const { initials } = useSelector(selectUnitOfMeasurement)

  const { data: plots, mutate, isValidating } = useGaloMonitorApi('/farms/plots/properties')

  const userActions = useMemo(
    () => getActionsBySlug(routes.maps.slug),
    [getActionsBySlug, routes.maps.slug]
  )

  const columns = useMemo(
    () => [
      {
        title: t('name'),
        field: 'plot.name'
      },
      {
        title: t('plot classification'),
        field: 'plot.plotType.name',
        render: ({ plot: { plotType } }) => t('plot classification types', { type: PLOT_CLASSIFICATION_TYPES[plotType.name] })
      },
      {
        title: `${ t('area', { howMany: 1 }) } (${ t(`unit of measurement label ${ initials }`) })`,
        field: 'convertedArea'
      }
    ],
    [initials, t]
  )

  const onAddClick = useCallback(
    () => {
      createDialog({
        id: 'create-plot',
        Component: PlotModal,
        props: {
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onEditClick = useCallback(
    (_, { plot }) => {
      createDialog({
        id: 'edit-plot',
        Component: PlotModal,
        props: {
          plot,
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onDeleteClick = useCallback(
    (_, { plot }) => {
      createDialog({
        id: 'confirmation',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to delete the "{name}" {this}?', {
            gender: 'male',
            howMany: 1,
            this: t('plot', { howMany: 1 }),
            name: plot.name
          }),
          onConfirm: () => {
            dispatch(PropertyActions.removePropertyPlot(
              plot,
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: t('plot', { howMany: 1 }),
                  action: toLower(t('removed', { gender: 'male', howMany: 1 }))
                }))
                mutate()
              },
              () => {
                createDialog({
                  id: 'plots-error',
                  Component: ErrorModal,
                  props: {
                    title: `${ t('could not {action} the {this}', {
                      howMany: 1,
                      gender: 'male',
                      action: toLower(t('remove')),
                      this: toLower(t('plot', { howMany: 1 }))
                    })  }.`
                  }
                })
              }
            ))
          }
        }
      })
    },
    [createDialog, dispatch, mutate, snackbar, t]
  )

  const onImportClick = useCallback(
    () => {
      createDialog({
        id: 'import-plot',
        Component: ImportMapModal,
        props: {
          onSuccess: (plotData) => createDialog({
            id: 'create-plot',
            Component: PlotModal,
            props: {
              type: 'plot',
              plot: plotData,
              onSuccess: mutate
            }
          })
        }
      })
    },
    [createDialog, mutate]
  )

  useEffect(() => {
    mutate()
  }, [currentProperty, mutate])

  return (
    <DataTable
      loading={ isValidating }
      data={ plots || [] }
      columns={ columns }
      onAddClick={ userActions.write && onAddClick }
      onMultipleActionsPopover={ [
        {
          text: t('create {this}', { this: t('plot', { howMany: 1 }) }),
          onClick: onAddClick
        },
        {
          text: t('import {this}', { this: t('plot', { howMany: 1 }) }),
          onClick: onImportClick
        }
      ] }
      onEditClick={ userActions.edit && onEditClick }
      onDeleteClick={ userActions.delete && onDeleteClick }
    />
  )
}

export default Plots
